"use client";

import { useKyc, useVerifyIdentityAction } from "@/lib/kyc/hooks";
import { Icon, type IconName } from "@/components/ui/Icon";
import { Button } from "@/components/ui/primitives";

/* The whitelabel's KycVerifyIdentityBanner, above the withdraw form: while a
   verification is open it says so and offers to continue it; when the site
   requires a level the player has not reached, it asks for it. Nothing when
   KYC is off for the site or the player is clear. */
export function KycVerifyIdentityBanner() {
  const { kycEnabled, kycRequired, pendingVerification } = useKyc();
  const { verifyIdentity, isPending } = useVerifyIdentityAction();

  if (!kycEnabled) {
    return null;
  }

  if (pendingVerification) {
    return (
      <KycBanner
        icon="refresh"
        spinning
        title="Identity verification in progress"
        body="Withdrawals unlock once it's complete. If you left before finishing, you can pick up where you left off."
        action="Continue verification"
        onAction={verifyIdentity}
        isPending={isPending}
      />
    );
  }

  if (!kycRequired) {
    return null;
  }

  return (
    <KycBanner
      icon="verified"
      title="Verify your identity to withdraw"
      body="Identity verification is required before you can withdraw."
      action="Verify identity"
      onAction={verifyIdentity}
      isPending={isPending}
    />
  );
}

function KycBanner({
  icon,
  spinning,
  title,
  body,
  action,
  onAction,
  isPending,
}: {
  icon: IconName;
  spinning?: boolean;
  title: string;
  body: string;
  action: string;
  onAction: () => void;
  isPending: boolean;
}) {
  return (
    <div className="flex flex-col gap-[16px] rounded-lg bg-elevation-2 p-[16px] sm:flex-row sm:items-center">
      <div className="flex flex-1 items-start gap-[16px]">
        <Icon
          name={icon}
          size={spinning ? 16 : 24}
          className={spinning ? "mt-[2px] shrink-0 animate-spin text-fg-subtle" : "shrink-0 text-brand-fg-accent"}
        />
        <div className="flex flex-col gap-[2px]">
          <p className="text-[12px] font-bold leading-[1.6] text-fg">{title}</p>
          <p className="text-[12px] leading-[1.6] text-fg-subtle">{body}</p>
        </div>
      </div>
      <Button size="sm" onClick={onAction} disabled={isPending} className="w-full sm:w-auto">
        {isPending ? "Opening…" : action}
      </Button>
    </div>
  );
}
